import React, { useEffect, useMemo, useState } from "react";
import Image from "next/image";
import {
  MdFirstPage,
  MdLastPage,
  MdChevronRight,
  MdChevronLeft,
} from "react-icons/md";
import { getSupabase } from "../../../utils/supabase/client";
import {
  getKnownUploads,
  hasUploadedImage,
} from "../../../utils/supabase/uploads";

const PAGE_SIZE_OPTIONS = [12, 24, 48, 96];

const CosmicCollectionStars = ({ category, sectionLabel = "Items" }) => {
  const [items, setItems] = useState(null); // null = loading
  const [searchText, setSearchText] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(24);

  useEffect(() => {
    if (!category) return;
    let cancelled = false;
    (async () => {
      setItems(null);
      const supabase = getSupabase();
      const [res, known] = await Promise.all([
        supabase
          .from("niftysky_collections")
          .select("niftysky_id, title, slug, image, description")
          .eq("niftysky_category_id", Number(category))
          .eq("is_active", true)
          .order("title")
          .limit(1000),
        getKnownUploads(),
      ]);
      if (cancelled) return;
      if (res.error) console.error("CosmicCollectionStars fetch failed:", res.error);
      const filtered = (res.data || []).filter((d) =>
        hasUploadedImage(d.image, known)
      );
      setItems(filtered);
    })();
    return () => {
      cancelled = true;
    };
  }, [category]);

  // Apply search filter
  const filtered = useMemo(() => {
    if (!items) return null;
    const q = searchText.trim().toLowerCase();
    if (!q) return items;
    return items.filter((d) => (d.title || "").toLowerCase().includes(q));
  }, [items, searchText]);

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [searchText, limit]);

  const total = filtered ? filtered.length : 0;
  const pageCount = Math.max(1, Math.ceil(total / limit));
  const currentPage = Math.min(page, pageCount);
  const pageItems = useMemo(() => {
    if (!filtered) return [];
    const start = (currentPage - 1) * limit;
    return filtered.slice(start, start + limit);
  }, [filtered, currentPage, limit]);

  const loading = items === null;
  const totalLoaded = items ? items.length : 0;

  return (
    <section className="text-white w-full px-4 sm:px-6 lg:px-10 pb-20">
      {/* Controls bar */}
      <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-1 h-8 rounded-full bg-gradient-to-b from-blue-400 to-blue-500" />
          <h2 className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-sky-300 via-blue-400 to-blue-300 bg-clip-text text-transparent">
            {sectionLabel}
          </h2>
          {!loading && totalLoaded > 0 && (
            <span className="text-xs text-gray-500 bg-white/5 px-2.5 py-1 rounded-full tabular-nums">
              {totalLoaded.toLocaleString()} items
            </span>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-3 w-full lg:w-auto">
          {/* Search */}
          <div className="relative flex-1 lg:flex-none lg:w-72">
            <svg
              className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              aria-hidden
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
            <input
              type="text"
              className="w-full pl-10 pr-3 py-2.5 text-sm rounded-xl bg-black/30 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-sky-500/50 focus:border-blue-400 transition-all"
              placeholder={`Search ${sectionLabel.toLowerCase()}…`}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              aria-label={`Search ${sectionLabel}`}
            />
            {searchText && (
              <button
                onClick={() => setSearchText("")}
                aria-label="Clear search"
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-md text-gray-500 hover:text-white hover:bg-white/10 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>

          {/* Per-page selector */}
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500 whitespace-nowrap">Per page:</span>
            <div className="flex rounded-lg border border-white/10 overflow-hidden">
              {PAGE_SIZE_OPTIONS.map((size) => (
                <button
                  key={size}
                  onClick={() => setLimit(size)}
                  className={`px-3 py-1.5 text-xs font-medium transition-all duration-200 ${
                    limit === size
                      ? "bg-gradient-to-r from-sky-500 to-blue-500 text-white"
                      : "bg-white/[0.03] text-gray-400 hover:bg-white/[0.06] hover:text-white"
                  }`}
                >
                  {size}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Grid */}
      {loading ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-5">
          {Array.from({ length: 8 }).map((_, i) => (
            <div
              key={i}
              className="rounded-2xl bg-white/5 animate-pulse aspect-[3/4]"
              style={{ animationDelay: `${i * 60}ms` }}
            />
          ))}
        </div>
      ) : pageItems.length > 0 ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-5">
          {pageItems.map((d, idx) => (
            <a
              key={d.niftysky_id || d.slug || idx}
              href={`/item-details/${d.slug}`}
              className="group relative rounded-2xl overflow-hidden border border-blue-800/20 bg-gradient-to-b from-gray-900/40 to-black/40 backdrop-blur-xl hover:border-sky-500/40 hover:shadow-2xl hover:shadow-sky-500/10 hover:-translate-y-1 transition-all duration-500 focus:outline-none focus:ring-2 focus:ring-sky-500/50"
            >
              {/* Image */}
              <div className="relative aspect-[3/4] w-full overflow-hidden bg-black/40">
                {d.image && (
                  <Image
                    src={d.image}
                    alt={d.title}
                    fill
                    sizes="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 25vw"
                    className="object-contain group-hover:scale-[1.05] transition-transform duration-700 ease-out"
                  />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent pointer-events-none" />
                <div className="absolute inset-0 bg-gradient-to-t from-sky-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
              </div>

              {/* Card body */}
              <div className="p-4 pb-5">
                <p className="text-sm sm:text-base font-semibold text-white group-hover:text-sky-200 transition-colors duration-300 text-center truncate">
                  {d.title}
                </p>
                <div className="mt-3 mx-auto h-0.5 w-10 bg-gradient-to-r from-[#3b82f6] to-[#60a5fa] rounded-full opacity-40 group-hover:opacity-100 group-hover:w-16 transition-all duration-500" />
              </div>
            </a>
          ))}
        </div>
      ) : (
        <div className="rounded-2xl border border-blue-800/20 bg-gradient-to-b from-gray-900/30 to-black/30 backdrop-blur-xl py-20 flex flex-col items-center justify-center text-gray-500">
          <svg className="w-12 h-12 mb-3 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <p className="text-sm">
            {searchText ? "No results match your search." : "No items in this catalogue."}
          </p>
          {searchText && (
            <button
              onClick={() => setSearchText("")}
              className="mt-3 text-xs text-blue-400 hover:text-sky-300 underline underline-offset-2"
            >
              Clear search
            </button>
          )}
        </div>
      )}

      {/* Pagination */}
      {!loading && pageCount > 1 && (
        <div className="mt-8 flex flex-col sm:flex-row items-center justify-between gap-3 pt-5 border-t border-white/5">
          <div className="text-sm text-gray-400">
            Showing{" "}
            <span className="text-white tabular-nums">{pageItems.length}</span> of{" "}
            <span className="text-white tabular-nums">{total.toLocaleString()}</span>
            {searchText && (
              <span className="text-xs text-gray-500 ml-1">
                (filtered from {totalLoaded.toLocaleString()})
              </span>
            )}
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setPage(1)}
              disabled={currentPage === 1}
              aria-label="First page"
              className="p-2 rounded-lg hover:bg-white/5 text-gray-400 hover:text-white disabled:opacity-30 disabled:pointer-events-none transition-colors"
            >
              <MdFirstPage size="1.1em" />
            </button>
            <button
              onClick={() => setPage((p) => Math.max(p - 1, 1))}
              disabled={currentPage === 1}
              aria-label="Previous page"
              className="p-2 rounded-lg hover:bg-white/5 text-gray-400 hover:text-white disabled:opacity-30 disabled:pointer-events-none transition-colors"
            >
              <MdChevronLeft size="1.1em" />
            </button>
            <span className="px-3 text-sm text-gray-300 tabular-nums">
              Page {currentPage} <span className="text-gray-500">of</span> {pageCount}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(p + 1, pageCount))}
              disabled={currentPage >= pageCount}
              aria-label="Next page"
              className="p-2 rounded-lg hover:bg-white/5 text-gray-400 hover:text-white disabled:opacity-30 disabled:pointer-events-none transition-colors"
            >
              <MdChevronRight size="1.1em" />
            </button>
            <button
              onClick={() => setPage(pageCount)}
              disabled={currentPage >= pageCount}
              aria-label="Last page"
              className="p-2 rounded-lg hover:bg-white/5 text-gray-400 hover:text-white disabled:opacity-30 disabled:pointer-events-none transition-colors"
            >
              <MdLastPage size="1.1em" />
            </button>
          </div>
        </div>
      )}
    </section>
  );
};

export default CosmicCollectionStars;
